import { ColDef } from 'ag-grid-community';
import { CurrencyDollar } from 'react-bootstrap-icons';
import { useCallback, useMemo } from 'react';
import { BaseReport } from './BaseReport';
import { ReportConfig } from './types';
import { Api } from '../Data/Api';
import { cents, showMoney } from '../Data/Money';
import { toLocalDateTime } from '../Data/Date';
import { PayoutSummaryReportApiResponse } from '../Data/ApiTypes';
import { ServerTransferSavedReportResponse } from '../shared/api/axios-client';
import {
    ReportFacilityMultiSelectRow,
    ReportFacilityOption,
    useReportFacilityMultiSelect,
} from './ReportFacilityMultiSelect';

interface PayoutSummaryFilter {
    page: number;
    pageSize: number;
    operatorId?: number;
    facilityIds?: number[];
    from?: string;
    to?: string;
}

type PayoutSummaryGridRow = {
    operatorId: number;
    facilityId: number;
    facilityName: string;
    payoutKey: string;
    payoutDate: PayoutSummaryReportApiResponse['payoutDate'];
    totalAmount: ReturnType<typeof cents>;
};

const columns: ColDef<PayoutSummaryGridRow>[] = [
    { field: 'facilityName', headerName: 'Facility', hide: false, filter: false },
    { field: 'payoutKey', headerName: 'Payout Key', hide: false, filter: false },
    {
        field: 'payoutDate',
        headerName: 'Payout Date',
        hide: false,
        filter: false,
        valueFormatter: (p) => toLocalDateTime(p.value),
    },
    {
        field: 'totalAmount',
        headerName: 'Total Amount',
        hide: false,
        filter: false,
        valueFormatter: (p) => showMoney(p.value),
    },
];

export function PayoutSummaryReport({
    operatorId,
    facilities,
    savedReports,
    setSavedReports,
    heightOffset = 50,
}: {
    operatorId: number;
    facilities?: ReportFacilityOption[];
    savedReports: ServerTransferSavedReportResponse[];
    setSavedReports: React.Dispatch<React.SetStateAction<ServerTransferSavedReportResponse[]>>;
    heightOffset?: number;
}) {
    // Every facility is selected by default; staff narrow the list from there.
    const allFacilityIds = useMemo(() => (facilities ?? []).map((f) => f.facilityId), [facilities]);
    const {
        facilityOptions,
        selectedOptions,
        selectedIds: currentFacilityIds,
        onMultiChange,
        selectAll,
        clearAll,
        setSelectedIds,
    } = useReportFacilityMultiSelect(facilities, allFacilityIds);

    const handleReset = useCallback(() => setSelectedIds(allFacilityIds), [setSelectedIds, allFacilityIds]);

    const config = useMemo<ReportConfig<PayoutSummaryGridRow, PayoutSummaryFilter>>(
        () => ({
            id: 'payoutsummary',
            title: 'Payout Summary Report',
            icon: <CurrencyDollar style={{ fontSize: '1.5rem', marginTop: '-10px' }} />,
            description: `<p>This report summarizes payouts by facility and payout key for the selected date range.</p>`,
            defaultColumns: columns,
            filterFields: [],
            facilities: facilityOptions.map((o) => ({ facilityId: o.value, name: o.label })),
            defaultFilterForm: {
                operatorId,
                from: '',
                to: '',
                quickDateMode: 'All',
            },
            getData: async (filter) => {
                if (!operatorId) {
                    throw new Error('Operator context is required');
                }
                const response = await Api.payoutSummaryReports({
                    ...filter,
                    operatorId,
                    facilityIds: currentFacilityIds.length > 0 ? currentFacilityIds : undefined,
                });
                if (!response.isOk) throw new Error(response.error);
                return {
                    total: response.value.total,
                    items: response.value.items.map((r) => ({
                        ...r,
                        totalAmount: cents(r.totalAmount),
                    })),
                };
            },
        }),
        [operatorId, facilityOptions, currentFacilityIds]
    );

    return (
        <>
            <ReportFacilityMultiSelectRow
                facilityOptions={facilityOptions}
                selectedOptions={selectedOptions}
                onMultiChange={onMultiChange}
                selectAll={selectAll}
                clearAll={clearAll}
            />
            <BaseReport
                config={config}
                facilityIds={currentFacilityIds}
                savedReports={savedReports}
                setSavedReports={setSavedReports}
                onFacilityIdsChange={setSelectedIds}
                onReset={handleReset}
                heightOffset={heightOffset}
                storageKey="report_columns_payout_summary"
            />
        </>
    );
}
